import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3144";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Réponse GET /U3144/initLU + logicalUnitCode fusionné côté shell. */
export interface U3144InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
}

/** Ligne POST /U3144/search. */
export interface U3144LotDetailOperationTypeSearchRow {
  id: string;
  operationTypeCode?: string;
  lotDetailSupplementaryInfoEnabled?: boolean;
  active?: boolean;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3144LotDetailOperationTypeTranslation {
  id?: string;
  languageCode: string;
  operationTypeLabel: string;
  version?: number;
}

/** Détail GET /U3144/:id (result). */
export interface U3144LotDetailOperationType {
  id: string;
  operationTypeCode: string;
  lotDetailSupplementaryInfoEnabled: boolean;
  active: boolean;
  translations: U3144LotDetailOperationTypeTranslation[];
  version: number;
}

export type U3144TranslationFormRow = U3144LotDetailOperationTypeTranslation;

export type U3144LotDetailOperationTypeFormRow = U3144LotDetailOperationType;

export interface U3144LotDetailOperationTypeCreateBody {
  operationTypeCode: string;
  lotDetailSupplementaryInfoEnabled: boolean;
  active: boolean;
  translations: Array<{
    languageCode: string;
    operationTypeLabel: string;
  }>;
}

export interface U3144LotDetailOperationTypeUpdateBody {
  id: string;
  operationTypeCode: string;
  lotDetailSupplementaryInfoEnabled: boolean;
  active: boolean;
  version: number;
  translations: Array<
    | {
        languageCode: string;
        operationTypeLabel: string;
      }
    | {
        id: string;
        languageCode: string;
        operationTypeLabel: string;
        version: number;
      }
  >;
}

export interface U3144LotDetailOperationTypeDeleteBody {
  id: string;
  version: number;
}

export const U3144_ERROR_CODE_IN_USE = "BU314401";
export const U3144_ERROR_CODE_DUPLICATE_CODE = "BU314402";

export function createEmptyU3144FormRow(): U3144LotDetailOperationTypeFormRow {
  return {
    id: "",
    operationTypeCode: "",
    lotDetailSupplementaryInfoEnabled: false,
    active: true,
    version: 0,
    translations: [{ languageCode: "", operationTypeLabel: "" }],
  };
}

export function normalizeU3144FromApi(row: U3144LotDetailOperationType): U3144LotDetailOperationType {
  return {
    ...row,
    active: typeof row.active === "boolean" ? row.active : true,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === "string" ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

export function buildCreateBody(data: U3144LotDetailOperationTypeFormRow): U3144LotDetailOperationTypeCreateBody {
  return {
    operationTypeCode: data.operationTypeCode.trim(),
    lotDetailSupplementaryInfoEnabled: data.lotDetailSupplementaryInfoEnabled,
    active: data.active !== false,
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      operationTypeLabel: tr.operationTypeLabel.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3144LotDetailOperationTypeFormRow): U3144LotDetailOperationTypeUpdateBody {
  return {
    id: data.id,
    operationTypeCode: data.operationTypeCode.trim(),
    lotDetailSupplementaryInfoEnabled: data.lotDetailSupplementaryInfoEnabled,
    active: data.active !== false,
    version: data.version,
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const operationTypeLabel = tr.operationTypeLabel.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== "");
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          operationTypeLabel,
          version: typeof tr.version === "number" ? tr.version : 0,
        };
      }
      return { languageCode, operationTypeLabel };
    }),
  };
}

export function buildDeleteBody(data: U3144LotDetailOperationTypeFormRow): U3144LotDetailOperationTypeDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3144LotDetailOperationTypeCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3144LotDetailOperationTypeUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3144LotDetailOperationTypeDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3144_ERROR_CODE_DUPLICATE_CODE:
      errorResult.fieldErrors["operationTypeCode"] = t("errOperationTypeCodeDuplicate", { ns: nameSpace });
      break;
    case U3144_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errDeleteInUse", { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
